import { useState } from "react";
import { Check, Loader2, ArrowRight } from "lucide-react";
import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { isMerchantHost, isProviderHost } from "@/lib/platformHosts";

// merchant.ordrs.ai / provider.ordrs.ai are marketing entry points into this
// same subscribe flow (see Landing.jsx's initialBusinessModeFromHost — same
// pattern). Landing on one just pre-selects the matching category below;
// it stays changeable in case someone reaches the wrong subdomain.
const HOST_PRESELECTED_CATEGORY = isMerchantHost ? "merchant" : isProviderHost ? "service_provider" : null;

export default function Subscribe() {
  const [plan, setPlan] = useState("establishment");
  const [installment, setInstallment] = useState(false);
  const [feeMode, setFeeMode] = useState("diner_150");
  const [locations, setLocations] = useState(2);
  const [setupMode, setSetupMode] = useState("build_new");
  const [existingSiteUrl, setExistingSiteUrl] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessCategory, setBusinessCategory] = useState(HOST_PRESELECTED_CATEGORY || "merchant");
  const [busy, setBusy] = useState(false);

  const setupToday = installment ? 150 : 899;
  const monthly = plan === "establishment" ? 179 : 99 * locations;

  const subscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post("/subscribe", {
        subscription_plan: plan,
        location_count: locations,
        setup_installment: installment,
        fee_mode: feeMode,
        existing_site_url: setupMode === "existing_site" ? existingSiteUrl || null : null,
        business_name: businessName || null,
        business_category: businessCategory,
        business_type: businessCategory === "service_provider" ? "service_provider" : "restaurant",
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <RevealNav />
      <section className="ds-container pt-32 pb-24">
        <div className="max-w-2xl mb-12">
          <p className="overline mb-4">subscribe</p>
          <h1 className="text-5xl lg:text-6xl mb-4">
            <span className="font-script text-primary text-5xl lg:text-6xl font-normal block leading-[1.05] mb-2">
              pick your plan
            </span>
            Pay now, set up later.
          </h1>
          <p className="text-lg text-muted-foreground">
            No account needed to subscribe. Pay first, then create your login
            and we'll build everything for you.
          </p>
        </div>

        <div className="max-w-xl">
          {/* Business name */}
          <Card className="p-6 mb-4">
            <p className="overline mb-3">business name</p>
            <Input
              placeholder="Your business name (e.g. Joe's Bakery)"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              data-testid="subscribe-business-name"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              We'll use this to create your storefront. Change it later.
            </p>
          </Card>

          {/* Business category — platform toggle */}
          <Card className="p-6 mb-4">
            <p className="overline mb-3">what kind of business?</p>
            <p className="text-xs text-muted-foreground mb-3">
              Choose the platform experience that matches your business model.
            </p>
            {[
              ["merchant", "Merchant", "Store / Restaurant / Retail",
                "Order management dashboard",
                "Take orders, manage your menu, accept payments, and fulfill online orders. Includes AI phone order-taking.",
              ],
              ["service_provider", "Service Provider", "Salon / Spa / Clinic / Studio",
                "Appointment booking CRM",
                "Manage bookings, schedule appointments, set staff availability, and accept reservations. Includes AI phone booking.",
              ],
            ].map(([k, title, subtitle, redirectLabel, description]) => (
              <label
                key={k}
                data-testid={`subscribe-category-${k}`}
                className={`block cursor-pointer mb-3 p-4 rounded-lg border-2 transition-colors ${
                  businessCategory === k
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{title}</span>
                    <span className="text-[11px] text-muted-foreground">{subtitle}</span>
                  </div>
                  {businessCategory === k && <Check className="w-4 h-4 text-primary" />}
                </div>
                <div className="text-xs text-muted-foreground mb-2">{description}</div>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-primary">
                  <ArrowRight className="w-3 h-3" />
                  {redirectLabel}
                </div>
                <input
                  type="radio"
                  name="business_category"
                  value={k}
                  checked={businessCategory === k}
                  onChange={() => setBusinessCategory(k)}
                  className="hidden"
                />
              </label>
            ))}
          </Card>{" "}


          {/* Setup path */}
          <Card className="p-6 mb-4">
            <p className="overline mb-3">website setup</p>
            {[
              ["build_new", "Build a new Ordrs site", "We'll create the full storefront and ordering experience."],
              ["existing_site", "Attach ordering to my current site", "Your current website stays live; Ordrs runs the order page."],
            ].map(([k, title, desc]) => (
              <label
                key={k}
                data-testid={`subscribe-setup-${k}`}
                className={`block cursor-pointer mb-2 p-3 rounded-lg border-2 transition-colors ${
                  setupMode === k ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold">{title}</span>
                  {setupMode === k && <Check className="w-4 h-4 text-primary" />}
                </div>
                <div className="text-xs text-muted-foreground">{desc}</div>
                <input
                  type="radio"
                  name="setup_mode"
                  value={k}
                  checked={setupMode === k}
                  onChange={() => setSetupMode(k)}
                  className="hidden"
                />
              </label>
            ))}
            {setupMode === "existing_site" && (
              <Input
                placeholder="https://your-current-site.com"
                value={existingSiteUrl}
                onChange={(e) => setExistingSiteUrl(e.target.value)}
                data-testid="subscribe-existing-site-url"
              />
            )}
            <p className="text-[11px] text-muted-foreground mt-2">
              The ordering domain is configured after account setup, usually as orders.yourbusiness.com.
            </p>
          </Card>

          {/* Plan */}
          <Card className="p-6 mb-4">
            <p className="overline mb-3">plan</p>
            {[
              ["establishment", "$179/mo", "Single location · full storefront"],
              ["enterprise", "$99/mo per location", "2+ locations · centralized dashboard"],
            ].map(([k, price, desc]) => (
              <label key={k} data-testid={`subscribe-plan-${k}`}
                className={`block cursor-pointer mb-2 p-3 rounded-lg border-2 transition-colors ${
                  plan === k ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                }`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold capitalize text-sm">{price}</span>
                  {plan === k && <Check className="w-4 h-4 text-primary" />}
                </div>
                <div className="text-xs text-muted-foreground">{desc}</div>
                <input type="radio" name="plan" value={k} checked={plan===k}
                  onChange={()=>setPlan(k)} className="hidden"/>
              </label>
            ))}
            {plan === "enterprise" && (
              <div className="mt-2">
                <label className="text-xs font-bold mb-1.5 block">Locations</label>
                <input type="number" min={2} value={locations}
                  onChange={e=>setLocations(parseInt(e.target.value)||2)}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  data-testid="subscribe-locations"/>
              </div>
            )}
          </Card>

          {/* Setup fee */}
          <Card className="p-6 mb-4">
            <p className="overline mb-3">setup fee</p>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-bold">{installment ? "$150 × 6 mo" : "$899 once"}</div>
                <div className="text-[11px] text-muted-foreground">{installment ? "Lower upfront cost" : "Save vs installment"}</div>
              </div>
              <Switch checked={installment} onCheckedChange={setInstallment} data-testid="subscribe-installment"/>
            </div>
          </Card>

          {/* Fee mode */}
          <Card className="p-6 mb-4">
            <p className="overline mb-3">transaction fee</p>
            {[["diner_150","Pass $1.50 to customer","Most popular · net-zero overhead"],
              ["platform_1pct","1% absorbed by you","Cleaner checkout for customers"]].map(([k,t,d]) => (
              <label key={k} data-testid={`subscribe-fee-${k}`}
                className={`block cursor-pointer mb-2 p-3 rounded-lg border-2 transition-colors ${
                  feeMode===k?"border-primary bg-primary/5":"border-border hover:border-primary/50"
                }`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold">{t}</span>
                  {feeMode===k && <Check className="w-4 h-4 text-primary"/>}
                </div>
                <div className="text-xs text-muted-foreground">{d}</div>
                <input type="radio" name="fee" value={k} checked={feeMode===k}
                  onChange={()=>setFeeMode(k)} className="hidden"/>
              </label>
            ))}
          </Card>

          {/* Due today + CTA */}
          <Card className="p-6 tracing-beam">
            <p className="overline mb-3">due today</p>
            <div className="text-4xl font-extrabold mb-1">
              ${(setupToday + monthly).toFixed(0)}
            </div>
            <div className="text-xs text-muted-foreground mb-4 space-y-1">
              <div className="flex justify-between">
                <span>Setup fee</span>
                <span className="font-semibold">${installment ? "150" : "899"}{installment ? "/mo × 6" : " once"}</span>
              </div>
              <div className="flex justify-between">
                <span>First month subscription</span>
                <span className="font-semibold">${monthly}/mo</span>
              </div>
              <hr className="opacity-30 my-1" />
              <div className="flex justify-between text-[11px]">
                <span>Ongoing after today</span>
                <span>${monthly}/mo{installment ? " + $150/mo setup × 5" : ""}</span>
              </div>
            </div>
            <Button disabled={busy} onClick={subscribe}
              className="w-full h-11 font-bold" data-testid="subscribe-pay-now">
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Subscribe now <ArrowRight className="w-4 h-4 ml-1"/></>}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2 text-center">
              Secure checkout via Stripe. No account needed.
            </p>
          </Card>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
